import { useRef } from 'react';

import { UNSAFE_noExposureExp } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import {
	CREATION_FOCUSED_NAV_SHOULD_SHOW_SPOTLIGHT,
	CREATION_FOCUSED_NAV_START_TIME,
	NAV_EXPERIMENT_ONBOARDING_KEYS,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';

export const d1 = 'd1';
export const loading = 'loading';
export const d0 = 'd0';
export const none = 'none';

type UseCfind4473CreationFocusedNavResult = {
	isSpotlightVisible: boolean;
	areBannersHidden: boolean;
	navCustomization: typeof d1 | typeof loading | typeof d0 | typeof none;
	onSpotlightShown?: () => void;
};

export const useCfind4473CreationFocusedNav = (
	updateState: boolean = false,
): UseCfind4473CreationFocusedNavResult => {
	const { setOnboardingState } = useSetOnboardingState();
	const { cohort, delayInMinutes } = getExperimentData();
	const hasUpdatedState = useRef(false);
	const isInExperimentControl = cohort !== 'treatment';

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		fg('cc_activation_simplify_nav_preloader_enabled')
			? [...NAV_EXPERIMENT_ONBOARDING_KEYS]
			: [CREATION_FOCUSED_NAV_SHOULD_SHOW_SPOTLIGHT, CREATION_FOCUSED_NAV_START_TIME],
		isInExperimentControl,
	);

	const spotlightShown = () => {
		setOnboardingState({
			key: CREATION_FOCUSED_NAV_SHOULD_SHOW_SPOTLIGHT,
			value: 'false',
		});
	};

	if (isInExperimentControl) {
		return { isSpotlightVisible: false, areBannersHidden: false, navCustomization: none };
	}

	if (onboardingStateLoading) {
		// Simplify nav and hide banners while loading, to minimize flashing
		return { isSpotlightVisible: false, areBannersHidden: true, navCustomization: loading };
	}

	if (onboardingStateError) {
		// Control behavior if there are any errors
		return { isSpotlightVisible: false, areBannersHidden: false, navCustomization: none };
	}

	const { shouldShowSpotlight, startTime } = deserializeState(onboardingStateData);

	if (isSiteOlderThanDelay(delayInMinutes, startTime)) {
		if (shouldShowSpotlight) {
			// Spotlight ready to show --> Show spotlight, bring nav features back, but don't bring banners back
			return {
				isSpotlightVisible: true,
				areBannersHidden: true,
				navCustomization: d1,
				onSpotlightShown: spotlightShown,
			};
		} else if (shouldShowSpotlight === false) {
			// Spotlight has already shown --> Don't show spotlight, bring nav features back, but don't bring banners back
			return { isSpotlightVisible: false, areBannersHidden: true, navCustomization: d1 };
		}
		// This account was created before the experiment started, so we won't change anything even though statsig returned "treatment"
		return { isSpotlightVisible: false, areBannersHidden: false, navCustomization: none };
	}

	// D0 site + user is in treatment --> set spotlight to show later, hide banners, and simplify nav
	else {
		if (shouldShowSpotlight === undefined && updateState && !hasUpdatedState.current) {
			hasUpdatedState.current = true;
			setOnboardingState({
				key: CREATION_FOCUSED_NAV_SHOULD_SHOW_SPOTLIGHT,
				value: 'true',
			});
		}
	}

	return {
		isSpotlightVisible: false,
		areBannersHidden: true,
		navCustomization: d0,
	};
};

type Cohort = 'control' | 'treatment';
type ExperimentData = {
	cohort: Cohort;
	delayInMinutes: number;
};

const getExperimentData = (): ExperimentData => {
	// eslint-disable-next-line confluence-feature-gating/unsafe-no-exposure
	const [expConfig] = UNSAFE_noExposureExp('cfind-4473-creation-focused-nav'); // Experiment exposure is fired in the backend
	const cohort = expConfig.get('cohort', 'control');
	const minutesPerHour = 60;
	const hoursPerDay = 24;
	const defaultDelayInMinutes = minutesPerHour * hoursPerDay;
	const delayInMinutes = expConfig.get('delayInMinutes', defaultDelayInMinutes.toString());
	return { cohort: cohort as Cohort, delayInMinutes: parseInt(delayInMinutes) };
};

const isSiteOlderThanDelay = (
	delayInMinutes: number,
	siteActivationDateString?: string,
): boolean => {
	if (!siteActivationDateString) {
		return true; // If site has no activation date, assume it is old
	}
	const siteActivationDateNumber = Number(siteActivationDateString);
	const siteActivationDate = new Date(siteActivationDateNumber ?? '');
	if (isNaN(siteActivationDate.getTime())) {
		return true; // If site has no activation date, assume it is old
	}
	const createdAtTimestamp = siteActivationDate.getTime();
	const millisecondsSinceCreation = Date.now() - createdAtTimestamp;
	const secondsPerMinute = 60;
	const millisecondsPerSecond = 1000;
	const millisecondsInDelayTime = delayInMinutes * secondsPerMinute * millisecondsPerSecond;
	return millisecondsSinceCreation > millisecondsInDelayTime;
};
